import type { Translations } from "../types";

export const en: Translations = {
    nav: {
        story: "Story",
        trailer: "Trailer",
        pillars: "Pillars",
        media: "Media",
        roadmap: "Roadmap",
        help: "Help",
    },
    cta: {
        wishlist: "Wishlist on Steam",
        newsletter: "Join the Newsletter",
        exploreMission: "Explore the Mission",
    },
    hero: {
        badge: "Pre Alpha // In Active Development",
        subheadline:
            "There is no rescue coming. The only option left is a planet that has broken every expedition before yours. This is Expedition 744.",
        scrollLabel: "Begin the Story",
        watchTrailer: "Watch Trailer",
        newsletterLabel: "Get development updates",
        newsletterReassurance: "No spam · Unsubscribe anytime",
    },
    story: {
        chapter1: {
            eyebrow: "Chapter I // The Collapse",
            headline: "They Warned Us. We Did Not Listen.",
            paragraphs: [
                "By 2099 the warnings had been mounting for decades. Climate breakdown, biosphere depletion, cascading resource shortages. Governments stalled. Corporations looked the other way. But the data kept pointing to the same conclusion.",
                "By 2145 the Earth had crossed a point of no return. Agriculture collapsed across multiple continents. Entire ocean systems destabilised. Whole regions became uninhabitable. The planet that had sustained us for millennia could no longer keep us alive.",
                "What began as a research programme was reborn as an emergency survival project. Its name was the GreenMars Initiative, and its purpose was no longer to study Mars. It was to make it liveable before time ran out.",
            ],
        },
        chapter2: {
            eyebrow: "Chapter II // The Expeditions",
            headline: "743 Missions Sent Before Yours. Almost None Survived.",
            paragraphs: [
                "Over the decades that followed, humanity poured its remaining industrial strength into Mars. Earlier expeditions built mining outposts, automated laboratories, and partial landing infrastructure. Every mission relied on whatever the previous one had left behind.",
                "The record, however, is catastrophic. Contact has been lost with nearly every crewed expedition. Some bases transmitted for months before falling silent. Others vanished during descent.",
                "One crew did return, in an emergency state. Their vessel was badly damaged, and only a single fragment of the debrief was ever made public:",
            ],
            quote: '"We are all lost. Never send anyone back to that cursed planet."',
            closing:
                "In spite of everything, Mars remains the best option humanity has left. And you are next.",
        },
        chapter3: {
            eyebrow: "Chapter III // Your Mission",
            headline: "You Do Not Explore Mars. You Rewrite It.",
            description:
                "Expedition 744 is not an exploration mission. It is a full scale planetary conversion mission. You bring engineers, scientists, field operators, automated construction systems, and the most advanced infrastructure package ever assembled for off world terraformation.",
            objectives: [
                "Establish a permanent industrial foothold on the surface",
                "Industrialize the surface and sustain every system the mission demands",
                "Alter the atmosphere, temperature, and magnetic shielding of an entire planet",
                "Seed biological systems and begin the ecological conversion of Mars",
            ],
        },
    },
    pillars: {
        eyebrow: "The Three Pillars",
        headline: "One Mission. Three Fronts.",
        videoCaption: "Ecological Conversion in Action",
        videoDescription: "Engineered compounds slowly reshape and enrich the Martian surface.",
        description:
            "Mars cannot be transformed by a single system. The mission rests on three interconnected pillars, each one fragile on its own, but together capable of bending a dead world towards life.",
        items: [
            {
                title: "Industrial Expansion",
                subtitle: "Build the Machine",
                description:
                    "Power generation, mining, fabrication, logistics, launch systems. Every machine on Mars must be built by your hands. Without industry, nothing else can move.",
            },
            {
                title: "Planetary Engineering",
                subtitle: "Reshape the World",
                description:
                    "Deploy megastructures that change Mars at a planetary scale. Orbital engines to shift its path around the Sun. Magnetic field networks to shield the atmosphere from solar stripping.",
            },
            {
                title: "Ecological Conversion",
                subtitle: "Bring Mars to Life",
                description:
                    "Process the atmosphere, liberate water, condition the soil, seed microbial life, and spread vegetation. Terraforming is not a switch you flip. It snowballs.",
            },
        ],
    },
    threats: {
        eyebrow: "The Opposition",
        headline: "Mars Does Not Want You Here.",
        description:
            "The planet pushes back at every level. Extreme cold. Lethal radiation. Dust storms. Atmospheric instability. And something else entirely. Unknown life has adapted to Martian conditions in ways no one predicted. It is territorial, resilient, and hostile.",
    },
    trailer: {
        eyebrow: "See It in Motion",
        headline: "Watch the Trailer",
    },
    media: {
        eyebrow: "From the Surface",
        headline: "Pre Alpha Transmissions",
        description:
            "Raw footage from the development front. Every screenshot is real, every system is being built. This is Expedition 744 taking shape.",
        captions: [
            "First industrial outpost established",
            "The Martian surface stretches endlessly",
            "Resource extraction operations begin",
            "Base expansion in hostile conditions",
            "Monitoring planetary conditions",
            "Manufacturing chains coming online",
            "The first signs of change",
        ],
    },
    roadmap: {
        eyebrow: "The Road Ahead",
        headline: "Where We Are and Where We Are Going",
        description:
            "Development is milestone driven, not deadline driven. We ship when things are ready, and we are transparent about the journey.",
        status: {
            completed: "Completed",
            current: "In Progress",
            upcoming: "Next Milestone",
            future: "Planned",
        },
        milestones: [
            {
                phase: "Foundation",
                title: "Core Systems and Prototype",
                description: "Establishing the fundamental gameplay systems and technical foundation.",
                items: [
                    "Core resource system",
                    "Terrain and environment rendering",
                    "Caving and underground systems",
                    "Early gameplay loop prototype",
                ],
            },
            {
                phase: "Current Focus",
                title: "Industrial and Planetary Systems",
                description:
                    "Building out the industrial expansion pillar and early planetary engineering mechanics.",
                items: [
                    "Manufacturing pipeline depth",
                    "Atmospheric simulation layer",
                    "Simple planetary terraforming mechanics",
                    "Planetary ocean and water systems",
                ],
            },
            {
                phase: "Next Milestone",
                title: "Towards Public Demo",
                description:
                    "A playable vertical slice that showcases the core vision and lets players experience the mission firsthand.",
                items: [
                    "Polished demo scenario",
                    "Onboarding flow",
                    "Performance optimisation",
                    "First PVE experience",
                ],
            },
            {
                phase: "Planned Direction",
                title: "Towards Early Access",
                description:
                    "Expanding all three pillars, deepening systems, and opening up long form gameplay progression.",
                items: [
                    "Full ecological conversion pillar",
                    "Advanced planetary engineering",
                    "Advanced planetary terraforming machines",
                    "Early access release on Steam",
                    "First playable version of the mission",
                ],
            },
            {
                phase: "Long Term Vision",
                title: "The Full Mission",
                description:
                    "The complete Expedition 744 experience. A deep, interconnected planetary conversion simulation.",
                items: [
                    "Complete three pillar integration",
                    "Full terraforming arc",
                    "Community driven evolution",
                ],
            },
        ],
    },
    wishlist: {
        eyebrow: "Join the Mission",
        headline: "The Expedition Needs You",
        description:
            "Expedition 744 is being built right now. Wishlisting on Steam is the single most impactful thing you can do. It tells the world that this mission matters.",
        reasons: [
            "Get notified the moment the demo is available",
            "Directly boost the game visibility on Steam",
            "Support an indie studio and help keep the project alive",
        ],
        cta: "Wishlist on Steam",
    },
    newsletter: {
        eyebrow: "Stay Connected",
        headline: "Receive Transmissions",
        description:
            "Subscribe for development updates from the surface. No spam, only genuine milestones and progress reports.",
        placeholder: "your@email.com",
        cta: "Subscribe",
        subscribing: "Subscribing...",
        successMessage: "Check your inbox — a confirmation link is on its way.",
        successDetail:
            "Click the link in the email to activate your subscription. The link expires in 24 hours.",
        spamNote: "Don't see the email? Check your spam or junk folder just in case.",
        confirmedMessage: "Transmission link established. Updates will follow.",
        confirmedDetail:
            "You're now on the list. Transmissions will reach you when there's something worth reporting.",
        unsubscribedMessage: "Subscription removed.",
        unsubscribedDetail:
            "This email address will no longer receive newsletter updates. You can subscribe again anytime.",
        reassurance: "No spam. Unsubscribe anytime. Only meaningful updates.",
        emailError: "Please enter a valid email address.",
        genericError: "Unable to subscribe right now. Please try again in a moment.",
        inlineButton: "Notify Me",
        inlineSuccess: "Check your inbox for a confirmation link.",
        inlineSuccessSpam: "Not there? Check your spam folder.",
    },
    footer: {
        closingLine: "Expedition 744. A 744 Interactive project, built with conviction.",
        navigation: "Navigation",
        connect: "Connect",
        legal: "Legal",
        legalNotice: "Legal Notice",
        privacyPolicy: "Privacy Policy",
        cookies: "Cookies",
        copyright: `© ${new Date().getFullYear()} 744 Interactive. All rights reserved.`,
    },
    legal: {
        pageTitle: "Legal",
        lastUpdated: "Last updated: April 2026",
        backHome: "Back to home",
        legalNotice: {
            title: "Legal Notice",
            rows: [
                { label: "Publisher", value: "744 Interactive" },
                {
                    label: "Publication director",
                    value: "Anonymous. Identity held by the hosting provider and available to judicial authorities on request (Art. 6 III LCEN).",
                },
                { label: "Contact", value: "contact@744interactive.com" },
                { label: "Hosting", value: "LWS (Label Web Solutions), lws.fr, France" },
            ],
        },
        privacy: {
            title: "Privacy Policy",
            sections: [
                {
                    title: "Data collected",
                    body: [
                        "Newsletter: email address, subscription date, confirmation status.",
                        "Support tickets: email address, category, subject, description, and optionally game version and platform.",
                        "Analytics: anonymous, aggregate page-view metrics via Vercel Analytics. No cookies, no personal data.",
                    ],
                },
                {
                    title: "Purpose and legal basis",
                    body: [
                        "Newsletter: sending development updates. Legal basis: consent (GDPR Art. 6-1-a).",
                        "Support: responding to requests. Legal basis: consent and legitimate interest (GDPR Art. 6-1-a and f).",
                        "Analytics: measuring site performance. Legal basis: legitimate interest (GDPR Art. 6-1-f).",
                    ],
                },
                {
                    title: "Retention",
                    body: [
                        "Newsletter data is deleted upon unsubscription.",
                        "Support tickets are kept for 3 years.",
                    ],
                },
                {
                    title: "Your rights",
                    body: [
                        "You have the right to access, correct, delete, or restrict your personal data. To do so, email contact@744interactive.com. We will respond within 30 days.",
                        "You can also file a complaint with the CNIL (cnil.fr).",
                    ],
                },
            ],
        },
        cookies: {
            title: "Cookies",
            body: [
                "This site uses minimal cookies and no tracking.",
                "Vercel Analytics is cookieless and stores no data on your device.",
                "You can delete cookies from your browser settings at any time.",
            ],
        },
    },
    help: {
        eyebrow: "Help & Support",
        title: "How Can We Help?",
        description:
            "Browse common questions below, or submit a support ticket if you need direct assistance. We read everything.",
    },
    faq: {
        title: "Frequently Asked Questions",
        items: [
            {
                category: "Game Access",
                question: "When can I play Expedition 744?",
                answer:
                    "The game is currently in pre alpha. A public demo is our next major milestone. Wishlist on Steam to be notified the moment it becomes available.",
            },
            {
                category: "Game Access",
                question: "Will there be a free demo?",
                answer:
                    "Yes, a free demo is planned as the first public release. It will showcase the core gameplay experience before Early Access.",
            },
            {
                category: "Technical",
                question: "What are the system requirements?",
                answer:
                    "System requirements will be published closer to the demo release. We are actively optimising to support a wide range of hardware.",
            },
            {
                category: "Technical",
                question: "The game is crashing or performing poorly. What should I do?",
                answer:
                    "Please submit a support ticket below with your system specifications, game version, and a description of the issue. Logs and screenshots help us diagnose things more quickly.",
            },
            {
                category: "Gameplay",
                question: "What kind of game is Expedition 744?",
                answer:
                    "It is a deep science fiction colony simulation centred on planetary conversion, combining industrial expansion, planetary engineering, and ecological terraforming on Mars.",
            },
            {
                category: "Gameplay",
                question: "Are there save or load issues I should know about?",
                answer:
                    "During pre alpha and demo phases, save formats may change between versions. We will always communicate when breaking changes happen.",
            },
            {
                category: "Feedback",
                question: "How can I share feedback or report a bug?",
                answer:
                    "Use the support form below. Select Bug Report or Feedback as the category. We read every submission.",
            },
        ],
    },
    support: {
        title: "Submit a Ticket",
        subtitle: "Describe your issue and we'll get back to you as soon as we can.",
        email: "Email",
        category: "Category",
        categoryPlaceholder: "Select a category",
        subject: "Subject",
        subjectPlaceholder: "Brief description of your issue",
        description: "Description",
        descriptionPlaceholder:
            "Describe the issue in detail. Steps to reproduce, expected vs actual behavior, etc.",
        gameVersion: "Game Version",
        gameVersionOptional: "(optional)",
        gameVersionPlaceholder: "e.g. 0.1.0-alpha",
        platform: "Platform / OS",
        platformOptional: "(optional)",
        platformPlaceholder: "e.g. Windows 11, Steam Deck",
        submit: "Send Ticket",
        submitting: "Sending...",
        successTitle: "Ticket Submitted",
        successDetail:
            "Thank you for reaching out. We've received your ticket and will respond to your email as soon as possible.",
        successAnother: "Submit another ticket",
        genericError: "Unable to submit your ticket right now. Please try again in a moment.",
        categories: ["Bug report", "Technical issue", "Demo access", "Feedback"],
    },
};
